import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useUser } from '../context/UserContext';
import { requestLeagueIntel } from '../utils/leagueIntelRequest';
import './LeagueIntelAssistant.css';

const promptSets = {
  dynasty: [
    'What is the highest-leverage move for my roster right now?',
    'Should I contend, retool or rebuild based on the current evidence?',
    'Who is a clean public roster fit for my biggest need?',
  ],
  lor: [
    'What is my highest-impact move this week?',
    'Where is my lineup most vulnerable right now?',
    'Who is a clean public roster fit for my biggest need?',
  ],
};

const restore = key => {
  try { return JSON.parse(sessionStorage.getItem(key) || '{}'); } catch { return {}; }
};

function FloatingBot({ busy, hasAnswer, hasError }) {
  const state = busy ? 'thinking' : hasError ? 'error' : hasAnswer ? 'ready' : 'idle';
  return <span className={`lia-avatar lia-avatar--${state}`} aria-hidden="true">
    <span className="lia-avatar-ring" />
    <svg viewBox="0 0 80 80">
      <defs><linearGradient id="floatingLeagueBot" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#d8ffff"/><stop offset="1" stopColor="#15939e"/></linearGradient></defs>
      <path className="lia-shell" d="M40 7 68 23v34L40 73 12 57V23z"/>
      <path className="lia-face" d="M40 17 59 28v24L40 63 21 52V28z"/>
      <path className="lia-signal" d="M29 35h22M31 52h18"/>
      <rect className="lia-eye" x="27" y="39" width="9" height="6" rx="3"/>
      <rect className="lia-eye" x="44" y="39" width="9" height="6" rx="3"/>
      <circle className="lia-core" cx="40" cy="53" r="3"/>
    </svg>
    <span className="lia-avatar-scan" />
  </span>;
}

export default function LeagueIntelAssistant({ api, league, enabled = true }) {
  const { currentUser } = useUser();
  const location = useLocation();
  const storageKey = `league-intel.popout.${league}.v1`;
  const saved = restore(storageKey);
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState('');
  const [history, setHistory] = useState(saved.history || []);
  const [result, setResult] = useState(saved.result || null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const label = league === 'lor' ? 'League of Record' : 'Dynasty';
  const fullPath = league === 'lor' ? '/league-of-record/league-desk' : '/league-desk';

  useEffect(() => {
    try { sessionStorage.setItem(storageKey, JSON.stringify({ history, result })); } catch {}
  }, [history, result, storageKey]);

  useEffect(() => { setOpen(false); }, [league]);

  const ask = async event => {
    event.preventDefault();
    const prompt = question.trim();
    if (!prompt || busy) return;
    setBusy(true); setError('');
    try {
      const payload = await requestLeagueIntel({
        api, path: '/api/league-agent/chat',
        body: { question: prompt, league, history: history.slice(-8) },
      });
      setResult(payload);
      window.dispatchEvent(new CustomEvent('leagueintel:telemetry', { detail: { event_type: 'agent_answer', target: 'public-assistant', metadata: { result: 'complete' } } }));
      setHistory(previous => [...previous, { role: 'user', content: prompt }, { role: 'assistant', content: payload.answer }].slice(-8));
      setQuestion('');
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  if (!enabled || !currentUser || currentUser.guest || location.pathname === fullPath) return null;

  return <aside className={`lia ${open ? 'lia--open' : ''}`}>
    {open && <section className="lia-panel" role="dialog" aria-modal="false" aria-label={`LeagueIntel ${label} assistant`}>
      <header>
        <FloatingBot busy={busy} hasAnswer={Boolean(result)} hasError={Boolean(error)} />
        <div><small>{label.toUpperCase()} · TEAM ADVISOR</small><strong>LeagueIntel</strong><span>{busy ? 'Reading the league…' : error ? 'Connection interrupted' : result ? 'Answer ready' : `Ask as ${currentUser.display_name || 'manager'}`}</span></div>
        <button type="button" onClick={() => setOpen(false)} aria-label="Close LeagueIntel">×</button>
      </header>
      <div className="lia-body">
        {!result && <div className="lia-prompts">{promptSets[league].map(prompt => <button type="button" key={prompt} onClick={() => setQuestion(prompt)}>{prompt}</button>)}</div>}
        {result && <article className="lia-answer" aria-live="polite">
          <div><small>{result.confidence} CONFIDENCE</small><h2>{result.headline}</h2></div>
          <p>{result.answer}</p>
          {result.recommendation && <div className="lia-call"><small>RECOMMENDATION</small><strong>{result.recommendation}</strong></div>}
        </article>}
        {error && <div className="lia-error" role="alert">{error}</div>}
      </div>
      <form onSubmit={ask}>
        <label htmlFor={`lia-question-${league}`}>Ask LeagueIntel</label>
        <textarea id={`lia-question-${league}`} value={question} onChange={event => setQuestion(event.target.value)} maxLength="2400" placeholder="Ask about your roster, a move, matchup or playoff path…" />
        <button type="submit" disabled={busy || !question.trim()}>{busy ? 'Analyzing…' : 'Ask'}</button>
      </form>
      <footer><span>Advice only · league-visible data</span><Link to={fullPath} onClick={() => setOpen(false)}>Open full evidence view →</Link></footer>
    </section>}
    <button className="lia-launcher" data-telemetry="leagueintel:launcher" type="button" onClick={() => { setOpen(value => !value); if (!open) window.dispatchEvent(new CustomEvent('leagueintel:telemetry', { detail: { event_type: 'agent_open', target: 'public-assistant' } })); }} aria-label={open ? 'Close LeagueIntel assistant' : 'Open LeagueIntel assistant'} aria-expanded={open}>
      <FloatingBot busy={busy} hasAnswer={Boolean(result)} hasError={Boolean(error)} />
      {!open && <span>ASK LEAGUEINTEL</span>}
    </button>
  </aside>;
}
